// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.

/**
 * Orchestration. Wires the UI to the live Tuner, the reference-tone pitch pipe,
 * and the file-analysis worker; owns the small amount of app state (settings +
 * the current report); and turns results into downloadable/shareable artefacts.
 * Thin by design — the real work lives in pitch/tuner/tone/analysis/report.
 */

import './styles/main.css';
import { buildApp } from './ui';
import { Tuner } from './tuner';
import { audioSupported, decodeAudioFile } from './audio';
import { TonePlayer, renderReferenceSequence } from './tone';
import { getInstrument, INSTRUMENT_ORDER, INSTRUMENTS } from './instruments';
import { midiToFrequency, noteLabel } from './notes';
import { drawDial } from './dial';
import { categoryLogger } from './eventlog';
import { encodeWavMono } from './wav';
import { canShareFiles, canvasToPng, copyImage, copyText, saveBlob, shareFiles } from './output';
import { formatCents, formatHz, sanitizeStem, baseName } from './format';
import { isTypingTarget } from './keyboard';
import { isIPhone, micAvailable } from './platform';
import { renderReportCard, summaryText, toCsv, type ReportMeta } from './report';
import type { AnalyseResult } from './analysis';
import type { AnalyseDone, AnalyseFailed, AnalyseProgress, AnalyseRequest } from './analysis-worker';
import type { Accidental, InstrumentId, PitchReading, Settings } from './types';

const log = categoryLogger('system');
const micLog = categoryLogger('mic');
const toneLog = categoryLogger('tone');
const fileLog = categoryLogger('file');
const exportLog = categoryLogger('export');

const SETTINGS_KEY = 'tunewell:settings';
const REF_OPTIONS = [415, 432, 440, 442, 444];
const WAV_RATE = 44100;

const DEFAULT_SETTINGS: Settings = { instrument: 'guitar', referenceA: 440, accidental: 'sharp' };

function loadSettings(): Settings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    const p = JSON.parse(raw) as Partial<Settings>;
    const instrument = (INSTRUMENT_ORDER as string[]).includes(p.instrument as string)
      ? (p.instrument as InstrumentId)
      : 'guitar';
    return {
      instrument,
      referenceA: REF_OPTIONS.includes(p.referenceA as number) ? (p.referenceA as number) : 440,
      accidental: p.accidental === 'flat' ? 'flat' : 'sharp',
    };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

function saveSettings(s: Settings): void {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(s));
  } catch {
    /* private mode — ignore */
  }
}

interface CurrentReport {
  result: AnalyseResult;
  meta: ReportMeta;
  canvas: HTMLCanvasElement;
}

function main(): void {
  const root = document.getElementById('app');
  if (!root) return;
  const ui = buildApp(root);

  const settings = loadSettings();
  const tone = new TonePlayer();
  let lastReading: PitchReading = { hz: null, clarity: 0, note: null, stringIndex: -1, inTune: false };
  let currentReport: CurrentReport | null = null;
  let playingStringIndex = -1;

  const tuner = new Tuner({ onReading: (r) => onReading(r), onError: (m) => micLog(m, 'warn') }, settings);

  // ── Feature support ─────────────────────────────────────────────────────
  if (!audioSupported()) {
    ui.startMicBtn.disabled = true;
    ui.unsupportedNote.hidden = false;
    ui.unsupportedNote.textContent = 'This browser has no Web Audio support, so the tuner cannot run.';
  } else if (!micAvailable()) {
    ui.startMicBtn.textContent = 'Microphone unavailable';
    ui.startMicBtn.disabled = true;
    ui.unsupportedNote.hidden = false;
    ui.unsupportedNote.textContent =
      'The microphone needs a secure (https) connection. You can still tune by ear or analyse a recording.';
  }
  log('Ready. Nothing is uploaded — everything runs on this device.', 'ok');

  // ── Build the instrument selectors (start + live) ────────────────────────
  buildInstrumentSeg(ui.instrumentStart);
  buildInstrumentSeg(ui.instrumentLive);
  buildRefSeg(ui.refGroup);
  syncSettingControls();

  function buildInstrumentSeg(container: HTMLElement): void {
    container.innerHTML = '';
    for (const id of INSTRUMENT_ORDER) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'seg-btn';
      btn.dataset.inst = id;
      btn.textContent = INSTRUMENTS[id].name;
      btn.addEventListener('click', () => setInstrument(id));
      container.appendChild(btn);
    }
  }

  function buildRefSeg(container: HTMLElement): void {
    container.innerHTML = '';
    for (const hz of REF_OPTIONS) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'seg-btn';
      btn.dataset.ref = String(hz);
      btn.textContent = String(hz);
      btn.addEventListener('click', () => setReferenceA(hz));
      container.appendChild(btn);
    }
  }

  function setInstrument(id: InstrumentId): void {
    settings.instrument = id;
    tuner.updateSettings(settings);
    saveSettings(settings);
    syncSettingControls();
    buildStringRow();
    onReading(lastReading);
  }
  function setReferenceA(hz: number): void {
    settings.referenceA = hz;
    tuner.updateSettings(settings);
    saveSettings(settings);
    syncSettingControls();
    buildStringRow();
    if (tone.playing && playingStringIndex >= 0) playStringTone(playingStringIndex);
    onReading(lastReading);
  }
  function setAccidental(a: Accidental): void {
    settings.accidental = a;
    tuner.updateSettings(settings);
    saveSettings(settings);
    syncSettingControls();
    buildStringRow();
    onReading(lastReading);
  }

  ui.accidentalBtn.addEventListener('click', () => setAccidental(settings.accidental === 'sharp' ? 'flat' : 'sharp'));

  function syncSettingControls(): void {
    for (const c of [ui.instrumentStart, ui.instrumentLive]) {
      c.querySelectorAll<HTMLElement>('.seg-btn').forEach((b) => b.classList.toggle('on', b.dataset.inst === settings.instrument));
    }
    ui.refGroup.querySelectorAll<HTMLElement>('.seg-btn').forEach((b) => b.classList.toggle('on', b.dataset.ref === String(settings.referenceA)));
    ui.accidentalBtn.textContent = settings.accidental === 'sharp' ? '♯ sharps' : '♭ flats';
  }

  // ── Reference-tone pitch pipe ────────────────────────────────────────────
  function pipeFrequencies(): { freqs: number[]; labels: string[] } {
    const inst = getInstrument(settings.instrument);
    if (inst.strings.length === 0) {
      // Chromatic: a single A reference at the chosen pitch.
      return { freqs: [settings.referenceA], labels: [`A4 · ${settings.referenceA} Hz`] };
    }
    return {
      freqs: inst.strings.map((s) => midiToFrequency(s.midi, settings.referenceA)),
      labels: inst.strings.map((s) => noteLabel(s.midi, settings.accidental)),
    };
  }

  function buildStringRow(): void {
    const { freqs, labels } = pipeFrequencies();
    ui.stringRow.innerHTML = '';
    labels.forEach((label, i) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'string-btn';
      btn.dataset.idx = String(i);
      btn.innerHTML = `<span class="string-name">${label}</span><span class="string-hz">${formatHz(freqs[i])} Hz</span>`;
      btn.addEventListener('click', () => togglePlayString(i));
      ui.stringRow.appendChild(btn);
    });
    ui.stringRowLabel.textContent =
      getInstrument(settings.instrument).strings.length === 0 ? 'Reference tone — tap to hear' : 'Reference tones — tap a string to hear';
  }

  function togglePlayString(i: number): void {
    if (tone.playing && playingStringIndex === i) {
      stopTone();
      return;
    }
    playStringTone(i);
  }

  function playStringTone(i: number): void {
    const { freqs, labels } = pipeFrequencies();
    if (i < 0 || i >= freqs.length) return;
    const ok = tone.play(freqs[i]);
    if (!ok) {
      ui.showToast('Reference tones are not supported in this browser.');
      return;
    }
    playingStringIndex = i;
    ui.stopToneBtn.hidden = false;
    highlightStrings();
    toneLog(`Playing reference ${labels[i]} (${formatHz(freqs[i])} Hz).`, 'ok');
  }

  function stopTone(): void {
    tone.stop();
    playingStringIndex = -1;
    ui.stopToneBtn.hidden = true;
    highlightStrings();
  }
  ui.stopToneBtn.addEventListener('click', stopTone);

  function highlightStrings(): void {
    ui.stringRow.querySelectorAll<HTMLElement>('.string-btn').forEach((b) => {
      const idx = Number(b.dataset.idx);
      b.classList.toggle('playing', idx === playingStringIndex);
      const detected = idx === lastReading.stringIndex && lastReading.clarity > 0;
      b.classList.toggle('detected', detected);
      b.classList.toggle('in-tune', detected && lastReading.inTune);
    });
  }

  ui.downloadWavBtn.addEventListener('click', async () => {
    const { freqs } = pipeFrequencies();
    const inst = getInstrument(settings.instrument);
    const buffer = renderReferenceSequence(freqs, WAV_RATE);
    const wav = encodeWavMono(buffer, WAV_RATE);
    const blob = new Blob([wav], { type: 'audio/wav' });
    const stem =
      inst.strings.length === 0
        ? `chromatic-a${settings.referenceA}-reference`
        : `${inst.id}-reference-tones-a${settings.referenceA}`;
    const ok = await saveBlob(blob, { suggestedName: `${stem}.wav`, mime: 'audio/wav', description: 'WAV audio', ext: 'wav' });
    if (ok) exportLog(`Saved reference-tone pitch pipe (${inst.name}, A=${settings.referenceA}).`, 'ok');
  });

  // ── Live readings ────────────────────────────────────────────────────────
  function onReading(r: PitchReading): void {
    lastReading = r;
    drawDial(ui.dialCanvas, r);
    highlightStrings();

    if (r.note && r.clarity > 0) {
      ui.readoutNote.textContent = r.note.name;
      ui.readoutOct.textContent = String(r.note.octave);
      ui.readoutNote.dataset.tier = tierOf(r);
      const cents = formatCents(r.note.cents);
      ui.readoutCents.textContent = `${cents} cents`;
      ui.readoutCents.dataset.tier = tierOf(r);
      ui.readoutHz.textContent = `${formatHz(r.hz)} Hz`;
      ui.readoutStatus.textContent = r.inTune ? 'In tune' : r.note.cents < 0 ? 'Tune up ↑' : 'Tune down ↓';
      ui.readoutStatus.dataset.tier = tierOf(r);
    } else {
      ui.readoutNote.textContent = '–';
      ui.readoutOct.textContent = '';
      delete ui.readoutNote.dataset.tier;
      ui.readoutCents.textContent = tuner.isRunning ? 'Play a note…' : 'Tap Start microphone, or tune by ear below.';
      delete ui.readoutCents.dataset.tier;
      ui.readoutHz.textContent = '— Hz';
      ui.readoutStatus.textContent = '';
    }
  }

  function tierOf(r: PitchReading): string {
    if (!r.note) return 'muted';
    if (r.inTune) return 'in';
    return r.note.cents < 0 ? 'flat' : 'sharp';
  }

  // ── Enter live / start mic ───────────────────────────────────────────────
  function enterLive(): void {
    ui.setState('live');
    buildStringRow();
    onReading(lastReading);
    requestAnimationFrame(() => drawDial(ui.dialCanvas, lastReading));
  }

  async function startMic(): Promise<void> {
    ui.liveMicBtn.disabled = true;
    ui.startMicBtn.disabled = true;
    try {
      tuner.updateSettings(settings);
      await tuner.startMic();
      micLog('Microphone open — analysing live, recording nothing.', 'ok');
      ui.liveMicBtn.textContent = 'Stop microphone';
      ui.liveMicBtn.classList.add('recording');
      if (isIPhone(navigator.userAgent)) {
        ui.micNote.hidden = false;
        ui.micNote.textContent =
          'On iPhone, reference tones play through the earpiece while the mic is on (an iOS limitation). Hold it to your ear, use headphones, or download the pitch-pipe WAV.';
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'The microphone could not be started.';
      micLog(msg, 'err');
      ui.showToast(msg);
      ui.micNote.hidden = false;
      ui.micNote.textContent = msg;
    } finally {
      ui.liveMicBtn.disabled = false;
      ui.startMicBtn.disabled = !micAvailable();
    }
  }

  function stopMic(): void {
    tuner.stop();
    ui.liveMicBtn.textContent = 'Start microphone';
    ui.liveMicBtn.classList.remove('recording');
    onReading({ hz: null, clarity: 0, note: null, stringIndex: -1, inTune: false });
    micLog('Microphone stopped.', 'info');
  }

  ui.startMicBtn.addEventListener('click', async () => {
    enterLive();
    await startMic();
  });
  ui.tuneByEarBtn.addEventListener('click', () => {
    enterLive();
    ui.micNote.hidden = false;
    ui.micNote.textContent = 'Tuning by ear — tap a string to hear its reference tone, or start the microphone above.';
  });
  ui.liveMicBtn.addEventListener('click', () => {
    if (tuner.isRunning) stopMic();
    else startMic();
  });
  ui.backBtn.addEventListener('click', () => {
    stopTone();
    stopMic();
    ui.micNote.hidden = true;
    ui.setState('start');
  });

  // ── File / sample input ─────────────────────────────────────────────────
  ui.dropzone.addEventListener('click', () => ui.fileInput.click());
  ui.dropzone.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      ui.fileInput.click();
    }
  });
  ui.fileInput.addEventListener('change', () => {
    const file = ui.fileInput.files?.[0];
    if (file) handleFile(file);
    ui.fileInput.value = '';
  });
  ui.sampleBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    loadSample();
  });
  const dragOver = (e: DragEvent) => {
    e.preventDefault();
    ui.dropzone.classList.add('drag');
  };
  ui.dropzone.addEventListener('dragover', dragOver);
  ui.dropzone.addEventListener('dragleave', () => ui.dropzone.classList.remove('drag'));
  ui.dropzone.addEventListener('drop', (e) => {
    e.preventDefault();
    ui.dropzone.classList.remove('drag');
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  });
  window.addEventListener('dragover', (e) => e.preventDefault());
  window.addEventListener('drop', (e) => e.preventDefault());

  async function loadSample(): Promise<void> {
    try {
      fileLog('Loading the sample recording…', 'info');
      const res = await fetch('samples/guitar-e2.wav');
      if (!res.ok) throw new Error('Could not load the sample.');
      const blob = await res.blob();
      handleFile(new File([blob], 'guitar-e2.wav', { type: 'audio/wav' }));
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not load the sample.';
      fileLog(msg, 'err');
      ui.showToast(msg);
    }
  }

  let worker: Worker | null = null;
  let jobId = 0;

  async function handleFile(file: File): Promise<void> {
    stopTone();
    stopMic();
    ui.setState('analyzing');
    ui.progressBar.style.width = '4%';
    ui.progressLabel.textContent = `Decoding “${file.name}”…`;
    fileLog(`Analysing ${file.name} (${Math.round(file.size / 1024)} KB).`, 'info');

    let decoded;
    try {
      decoded = await decodeAudioFile(file);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'That file could not be decoded.';
      fileLog(msg, 'err');
      ui.showToast(msg);
      ui.setState('start');
      return;
    }
    if (decoded.duration < 0.3) {
      ui.showToast('That clip is too short to analyse — try one at least half a second long.');
      ui.setState('start');
      return;
    }

    ui.progressLabel.textContent = 'Detecting pitch…';
    const id = ++jobId;
    if (!worker) worker = new Worker(new URL('./analysis-worker.ts', import.meta.url), { type: 'module' });
    const w = worker;

    const onMessage = (ev: MessageEvent<AnalyseProgress | AnalyseDone | AnalyseFailed>) => {
      const data = ev.data;
      if (data.id !== id) return;
      if (data.type === 'progress') {
        ui.progressBar.style.width = `${Math.round(8 + data.fraction * 90)}%`;
      } else if (data.type === 'done') {
        w.removeEventListener('message', onMessage);
        ui.progressBar.style.width = '100%';
        showReport(data.result, file.name);
      } else {
        w.removeEventListener('message', onMessage);
        fileLog(data.message, 'err');
        ui.showToast(data.message);
        ui.setState('start');
      }
    };
    w.addEventListener('message', onMessage);
    const samples = decoded.samples;
    const req: AnalyseRequest = {
      id,
      samples,
      sampleRate: decoded.sampleRate,
      opts: { referenceA: settings.referenceA, accidental: settings.accidental },
    };
    w.postMessage(req, [samples.buffer]);
  }

  // ── Report ────────────────────────────────────────────────────────────────
  function showReport(result: AnalyseResult, source: string): void {
    const meta: ReportMeta = {
      source,
      referenceA: settings.referenceA,
      accidental: settings.accidental,
      when: new Date().toLocaleString(),
    };
    const canvas = document.createElement('canvas');
    renderReportCard(canvas, result, meta);
    currentReport = { result, meta, canvas };
    ui.reportImg.src = canvas.toDataURL('image/png');
    ui.reportSummary.innerHTML = reportSummaryHtml(result, meta);
    ui.shareBtn.hidden = typeof (navigator as unknown as { share?: unknown }).share !== 'function';
    ui.setState('report');
    const label = result.dominant ? noteLabel(result.dominant.midi, meta.accidental) : 'no clear pitch';
    fileLog(`Analysed ${source}: ${label}.`, 'ok');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function reportSummaryHtml(result: AnalyseResult, meta: ReportMeta): string {
    if (!result.dominant) {
      return `<div class="rs-big"><span class="rs-note-name">No clear pitch</span></div>
        <p class="rs-hint">Try a cleaner recording of a single sustained note.</p>`;
    }
    const label = noteLabel(result.dominant.midi, meta.accidental);
    const median = result.medianCents;
    const inTune = median !== null && Math.abs(median) <= 5;
    const centsText = median !== null ? `${formatCents(median)} cents` : '—';
    return `
      <div class="rs-big">
        <span class="rs-note-name" data-tier="${inTune ? 'in' : 'off'}">${label}</span>
        <span class="rs-cents" data-tier="${inTune ? 'in' : 'off'}">${centsText} median</span>
      </div>
      <p class="rs-hint">Dominant note across the clip, at A4 = ${meta.referenceA} Hz.
      ${inTune ? 'That is comfortably in tune.' : 'Nudge the string toward 0 cents.'}</p>`;
  }

  ui.dlPngBtn.addEventListener('click', async () => {
    if (!currentReport) return;
    try {
      const blob = await canvasToPng(currentReport.canvas);
      const ok = await saveBlob(blob, { suggestedName: `${reportStem()}.png`, mime: 'image/png', description: 'PNG image', ext: 'png' });
      if (ok) exportLog('Report PNG saved.', 'ok');
    } catch (err) {
      ui.showToast(err instanceof Error ? err.message : 'Could not save the image.');
    }
  });
  ui.dlCsvBtn.addEventListener('click', async () => {
    if (!currentReport) return;
    const csv = toCsv(currentReport.result, currentReport.meta);
    const blob = new Blob([csv], { type: 'text/csv' });
    const ok = await saveBlob(blob, { suggestedName: `${reportStem()}.csv`, mime: 'text/csv', description: 'CSV file', ext: 'csv' });
    if (ok) exportLog('Report CSV saved.', 'ok');
  });
  ui.copySummaryBtn.addEventListener('click', async () => {
    if (!currentReport) return;
    const ok = await copyText(summaryText(currentReport.result, currentReport.meta));
    ui.showToast(ok ? 'Summary copied.' : 'Copy failed — your browser blocked clipboard access.');
    if (ok) exportLog('Summary copied.', 'ok');
  });
  ui.copyImgBtn.addEventListener('click', async () => {
    if (!currentReport) return;
    try {
      const blob = await canvasToPng(currentReport.canvas);
      const ok = await copyImage(blob);
      ui.showToast(ok ? 'Report image copied.' : 'This browser cannot copy images — use Download instead.');
      if (ok) exportLog('Report image copied.', 'ok');
    } catch {
      ui.showToast('Could not copy the image.');
    }
  });
  ui.shareBtn.addEventListener('click', async () => {
    if (!currentReport) return;
    try {
      const blob = await canvasToPng(currentReport.canvas);
      const file = new File([blob], `${reportStem()}.png`, { type: 'image/png' });
      if (!canShareFiles([file])) {
        ui.showToast('Sharing is not available here — use Download.');
        return;
      }
      await shareFiles([file], 'Tunewell report', summaryText(currentReport.result, currentReport.meta));
      exportLog('Shared the report.', 'ok');
    } catch {
      ui.showToast('Sharing failed.');
    }
  });
  ui.newBtn.addEventListener('click', () => {
    currentReport = null;
    ui.setState('start');
  });

  function reportStem(): string {
    const src = currentReport?.meta.source;
    return src ? `${sanitizeStem(baseName(src))}-tuning` : 'tunewell-report';
  }

  // ── Keyboard ───────────────────────────────────────────────────────────────
  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      if (ui.isModalOpen()) ui.closeModal();
      else if (ui.isDrawerOpen()) ui.closeDrawer();
      else if (tone.playing) stopTone();
      return;
    }
    if (isTypingTarget(e.target)) return;
    if (e.key === ' ' && root.dataset.state === 'live') {
      e.preventDefault();
      if (tone.playing) stopTone();
      else playStringTone(Math.max(0, lastReading.stringIndex));
    }
  });

  window.addEventListener('resize', () => {
    if (root.dataset.state === 'live') drawDial(ui.dialCanvas, lastReading);
  });
  window.addEventListener('pagehide', () => {
    tuner.stop();
    tone.dispose();
  });

  // ── Test hooks (used by the Step-11 dry-run; harmless in production) ──────
  (window as unknown as { __tunewell?: unknown }).__tunewell = {
    injectFrame: (frame: Float32Array, sampleRate: number) => tuner.injectFrame(frame, sampleRate),
    enterLive: () => enterLive(),
    lastReading: () => ({ ...lastReading }),
    micFullyStopped: () => tuner.micFullyStopped(),
    settings: () => ({ ...settings }),
    pipeFrequencies: () => pipeFrequencies(),
    renderWav: () => {
      const { freqs } = pipeFrequencies();
      return encodeWavMono(renderReferenceSequence(freqs, WAV_RATE), WAV_RATE);
    },
    state: () => root.dataset.state,
  };
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', main);
} else {
  main();
}
